import type { HTMLAttributes, ReactNode } from 'react';

/**
 * Reveal-motion timing shared by every caller so the height transition and any
 * concurrent scroll stay synced from ONE source. `EXPAND_ANIMATION_MS` must stay
 * in sync with the `duration-300` reveal below: pass it as the scroll duration
 * (see `useScrollIntoViewOnReveal`) so the scroll and the expand share a duration
 * and finish together.
 */
export const EXPAND_ANIMATION_MS = 300;
/**
 * Decelerating curve shared by the reveal transition and the concurrent scroll
 * (the skill's recommended panel/slide easing). Avoids ease-in so motion never
 * lags the tap. Kept as a literal in the className below so Tailwind's JIT emits
 * the utility; call sites that need the same curve reference this constant.
 */
export const REVEAL_EASING = 'ease-[cubic-bezier(0.25,1,0.5,1)]';

/**
 * Animated reveal for collapsed overflow content. Uses the grid-template-rows
 * 0fr↔1fr trick so content height animates without measuring the DOM. The content
 * stays MOUNTED but is hidden from assistive tech and made non-focusable (`inert`)
 * while closed, and the motion is dropped under prefers-reduced-motion.
 *
 * Interpolating `grid-template-rows` is supported on the platforms Meetball ships (iOS ≥16
 * WKWebView - our floor - and current Chrome/Firefox/Safari). On older engines (sub-iOS-16,
 * old Android System WebView) it degrades to an instant reveal - the content is still
 * present and the inert/aria toggling still works; only the height interpolation is lost.
 *
 * Extra props (e.g. `id`, `data-testid`) spread onto the outer grid element so a toggle's
 * `aria-controls` can target it; the inner wrapper carries `data-reveal-content` so the
 * final height can be measured up front (see `useScrollIntoViewOnReveal`).
 */
export function AnimatedCollapse({
  open,
  children,
  ...rest
}: { open: boolean; children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none ${
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      }`}
      aria-hidden={!open}
      {...rest}
    >
      <div className="overflow-hidden" inert={!open} data-reveal-content>
        {children}
      </div>
    </div>
  );
}
